/**
 * DailyPanchangaView — the entire Daily Panchanga page, extracted from App.tsx
 * (B-048 decomposition). Owns the homa/tarabalam preload state + effects (they
 * exist only for this view); App keeps the panchanga fetch itself because the
 * ControlBar sunrise sync and the Kāla Chakra view share `data`.
 *
 * Golden-set view: any layout change here requires regenerating the Daily
 * Panchanga golden references (55-image visual regression).
 */
import React, { useState, useEffect } from 'react';
import { panchangaApi } from '../../services/api';
import { API_CONFIG } from '../../config/api';
import PanchangaCard from '../PanchangaCard/PanchangaCard';
import HomaRecommendations from '../HomaRecommendations/HomaRecommendations';
import Tarabalam from '../Tarabalam/Tarabalam';
import { nakshatraDay, withDayContext } from '../Tarabalam/dayContext';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import ErrorDisplay from '../ErrorDisplay/ErrorDisplay';
import FestivalsCard from './FestivalsCard';
import KalaViseshaCard from './KalaViseshaCard';
import GrahaPositions from './GrahaPositions';
import { LocationData } from '../LocationSearch/LocationSearch';
import type { Locale, PanchangaResponse, TaraBalamResponse } from '../../types/api';
import { uiString } from '../../i18n/uiStrings';

interface HomaData {
  recommended_gods: { name: string; benefit: string | null; source: string }[];
  recommendations_text: string;
  references: string;
}

interface DailyPanchangaViewProps {
  data: PanchangaResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  selectedDate: Date;
  selectedLocation: LocationData | null;
  selectedLocale: Locale;
  cameFromMonthly: boolean;
  onBackToMonthly: () => void;
  onViewAlignment: () => void;
  onNavigateToArticle: (articleId: string) => void;
}

export default function DailyPanchangaView({
  data, loading, error, refetch,
  selectedDate, selectedLocation, selectedLocale,
  cameFromMonthly, onBackToMonthly, onViewAlignment, onNavigateToArticle,
}: DailyPanchangaViewProps) {
  // The IAST weekday is the Homa rules' key (vaara_en when locale != 'en').
  const enVaara = data?.vaara_en || data?.vaara;

  // Preloaded data for summary cards + full components
  const [homaData, setHomaData] = useState<HomaData | null>(null);
  const [tarabalamFirst, setTarabalamFirst] = useState<TaraBalamResponse | null>(null);
  const [tarabalamSecond, setTarabalamSecond] = useState<TaraBalamResponse | null>(null);
  const [homaError, setHomaError] = useState<string | null>(null);
  const [tarabalamError, setTarabalamError] = useState<string | null>(null);

  // Homa and Tārā Bala by aṅga number from `angas` (B-139-REQ); the sentence
  // requests and their fallbacks were removed in B-139-CON.
  useEffect(() => {
    const tithiNumber = data?.angas?.tithi?.[0]?.number;
    const nakshatraNumber = data?.angas?.nakshatra?.[0]?.number;
    setHomaData(null);
    setHomaError(null);
    if (!tithiNumber || !nakshatraNumber || !enVaara) {
      if (data) setHomaError('Pañcāṅga details are unavailable for this day.');
      return;
    }
    let cancelled = false;
    const base = API_CONFIG.baseUrl || window.location.origin;
    fetch(`${base}/api/v1/homa/recommendations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tithi_number: tithiNumber, nakshatra_number: nakshatraNumber, vaara: enVaara, locale: selectedLocale }),
    })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`Homa: ${r.status}`))))
      .then(result => { if (!cancelled) setHomaData(result); })
      .catch(err => { if (!cancelled) setHomaError(err instanceof Error ? err.message : String(err)); });
    return () => { cancelled = true; };
  }, [data, enVaara, selectedLocale]);

  useEffect(() => {
    setTarabalamFirst(null);
    setTarabalamSecond(null);
    setTarabalamError(null);
    const day = data?.angas?.nakshatra && data?.date ? nakshatraDay(data.angas.nakshatra, data.date) : null;
    if (!day) {
      if (data) setTarabalamError('Pañcāṅga details are unavailable for this day.');
      return;
    }
    let cancelled = false;
    const localeParam = selectedLocale as 'en' | 'te' | 'kn' | 'sa';
    Promise.all([
      panchangaApi.getTarabalam({ nakshatra_number: day.first.number, locale: localeParam }),
      day.second ? panchangaApi.getTarabalam({ nakshatra_number: day.second.number, locale: localeParam }) : null,
    ])
      .then(([first, second]) => {
        if (cancelled) return;
        setTarabalamFirst(withDayContext(first, day));
        setTarabalamSecond(second);
      })
      .catch(err => { if (!cancelled) setTarabalamError(err instanceof Error ? err.message : String(err)); });
    return () => { cancelled = true; };
  }, [data, selectedLocale]);

  return (
    <>
      {cameFromMonthly && (
        <button
          onClick={onBackToMonthly}
          className="kg-link text-sm mb-2 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
        >
          &larr; Back to {selectedDate.toLocaleDateString('en-US', { month: 'long' })}
        </button>
      )}
      {loading && <LoadingSpinner message="Loading panchanga data..." />}
      {error && <ErrorDisplay message={error} onRetry={refetch} />}
      {data && !loading && (
        <div className="kg-warm space-y-4" style={{ color: 'var(--kg-ink)' }}>
          {/* Full-width page header — keeps both columns' first cards aligned at one top edge. */}
          <div className="flex items-baseline justify-between gap-3 px-1">
            <h2 className="font-fraunces text-[20px] font-medium" style={{ color: 'var(--kg-ink)' }}>{uiString(selectedLocale, 'dinaPanchanga')}</h2>
            <button
              onClick={() => onNavigateToArticle('how-to-panchanga')}
              className="kg-link shrink-0 text-[12.5px] font-semibold"
            >
              {uiString(selectedLocale, 'howToRead')}
            </button>
          </div>
          {/* 2-column grid: main card + full sections left, sticky summary cards right.
              Full sections live INSIDE the left column so the sticky sidebar's
              containing block spans the whole page height. */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3 space-y-4">
              <PanchangaCard panchanga={data} selectedLocation={selectedLocation} locale={selectedLocale} onViewAlignment={onViewAlignment} onNavigateToArticle={onNavigateToArticle} />
              {/* Festivals — mobile only (desktop shows in right column) */}
              {data.festivals && data.festivals.length > 0 && (
                <div className="lg:hidden">
                  <FestivalsCard festivals={data.festivals} />
                </div>
              )}
              {/* Kāla Viśeṣa — mobile only */}
              {data.intraday_windows && data.intraday_windows.length > 0 && (
                <div className="lg:hidden">
                  <KalaViseshaCard windows={data.intraday_windows} locale={selectedLocale} onLearnMore={() => onNavigateToArticle('kala-visesha')} />
                </div>
              )}
              <div id="homa-section">
                <HomaRecommendations
                  locale={selectedLocale}
                  preloadedData={homaData}
                  error={homaError}
                  onNavigateToArticle={onNavigateToArticle}
                />
              </div>
              {data.nakshatra && (
                <div id="tarabalam-section">
                  <Tarabalam locale={selectedLocale} preloadedData={tarabalamFirst} preloadedSecondData={tarabalamSecond} error={tarabalamError} onNavigateToArticle={onNavigateToArticle} />
                </div>
              )}
            </div>
            {/* Summary cards — desktop only */}
            {/* Sits 12 px below the sticky header and date bar, both measured (--kg-header-h,
                --kg-bar-h; B-153, B-153-SIDE); it used to pin under the date bar.
                will-change-transform promotes the sidebar to its own compositor layer:
                without it, Chromium sometimes stops painting sticky elements (blank
                until the next scroll repaint) when a backdrop-blur layer is involved. */}
            <div className="hidden lg:flex lg:flex-col lg:col-span-2 gap-4 lg:sticky lg:top-[calc(var(--kg-header-h,77px)_+_var(--kg-bar-h,0px)_+_12px)] lg:self-start will-change-transform">
              {data.festivals && data.festivals.length > 0 && (
                <FestivalsCard festivals={data.festivals} />
              )}
              {/* Kāla Viśeṣa — Intra-day Time Windows (Avoid | Favourable) */}
              {data.intraday_windows && data.intraday_windows.length > 0 && (
                <KalaViseshaCard windows={data.intraday_windows} locale={selectedLocale} onLearnMore={() => onNavigateToArticle('kala-visesha')} />
              )}
              <GrahaPositions date={selectedDate} location={selectedLocation} onOpenKalaChakra={onViewAlignment} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
